import { useMemo } from 'react';
import {
  TemplateImageSupport,
  buildImageUrlResolver,
} from '@/components/richText/image/templateImageSupport.ts';
import useGetTemplateImages from '@/features/dashboard/templateImages/hooks/useGetTemplateImages.ts';
import useUploadTemplateImage from '@/features/dashboard/templateImages/hooks/useUploadTemplateImage.ts';
import useDeleteTemplateImage from '@/features/dashboard/templateImages/hooks/useDeleteTemplateImage.ts';

// Bundles the template-image data layer into the shape `RichTextEditor` expects
// via its `imageSupport` prop. Pass `enabled = false` to skip fetching the media
// library where image support isn't shown (e.g. the SMS channel).
export default function useTemplateImageSupport(enabled = true): {
  imageSupport: TemplateImageSupport;
  imagesLoading: boolean;
} {
  const imagesQuery = useGetTemplateImages(enabled);
  const uploadMutation = useUploadTemplateImage();
  const deleteMutation = useDeleteTemplateImage();

  const images = imagesQuery.data;
  const { mutateAsync: uploadAsync } = uploadMutation;
  const { mutateAsync: deleteAsync } = deleteMutation;
  const isUploading = uploadMutation.isPending;

  const imageSupport = useMemo<TemplateImageSupport>(() => {
    const safeImages = images ?? [];
    return {
      images: safeImages,
      isUploading,
      upload: (file: File) => uploadAsync(file),
      remove: (fileId: string) => deleteAsync(fileId),
      resolveImageUrl: buildImageUrlResolver(safeImages),
    };
  }, [images, isUploading, uploadAsync, deleteAsync]);

  return {
    imageSupport,
    imagesLoading: enabled && imagesQuery.isLoading,
  };
}
